import { ApplicationConfig, isDevMode, inject, provideAppInitializer, ErrorHandler, provideZonelessChangeDetection } from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter, Router, withComponentInputBinding, withRouterConfig, withViewTransitions } from '@angular/router';
import { tap } from 'rxjs';
import { provideUniPinGql } from '@unipin/angular-gql';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { environment } from '@env/environment';
import { routes } from './app.routes';
import { TranslocoHttpLoader } from './transloco.loader';
import { TelemetryErrorHandler } from '@core/handlers/telemetry-error.handler';
import { redirectTo, redirectTo404 } from '@core/utils/route-redirector.util';
import { interceptHeader } from '@core/interceptors/header.interceptor';
import { fingerprintInterceptor } from '@core/interceptors/fingerprint.interceptor';
import { WebtopupService } from '@core/services/webptopup.service';
import { AppStore } from '@core/states/app/app.store';


export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: ErrorHandler,
      useClass: TelemetryErrorHandler,
    },
    
    provideZonelessChangeDetection(),
    
    provideHttpClient(
      withInterceptors(
        [
          interceptHeader,
          fingerprintInterceptor
        ]
      )
    ),
    
    provideRouter(
      routes,
      withViewTransitions(),
      withComponentInputBinding(),
      withRouterConfig({ onSameUrlNavigation: 'reload' }),
    ),

    provideUniPinGql(
      {
        useOperationPath: !localStorage.getItem('disableOperationPath'),
        uri: location.href.includes('unibox-in') || location.href.includes('india')? environment.indiaGqlUrl : environment.gqlUrl,
      }
    ),

    provideTransloco(
      {
        loader: TranslocoHttpLoader,
        config: {
          defaultLang: 'en',
          fallbackLang: 'en',
          prodMode: !isDevMode(),
          reRenderOnLangChange: false,
          flatten: {
            aot: !isDevMode()
          },
          missingHandler: {
            allowEmpty: true,
            useFallbackTranslation: true
          },
          availableLangs: [
            'ar',
            'bn',
            'br',
            'ch',
            'en',
            'es',
            'hi',
            'id',
            'kh',
            'ko',
            'lo',
            'mm',
            'ms',
            'nl',
            'ph',
            'ru',
            'si',
            'sv',
            'th',
            'tr',
            'ur',
            'vn',
            'zhhk',
            'zhtw',
            'zu',
          ]
        }
      }
    ),

    provideAppInitializer(
      () => {
        const router = inject(Router);
        const store = inject(AppStore);
        const service = inject(WebtopupService);
        const transloco = inject(TranslocoService);

        const guids = location.pathname.substring(1).split('/');
        if (
          !guids || 
          guids.length < 2
        ) {
          redirectTo404(router, '', '');
          return;
        }

        transloco.setActiveLang(
          (new URL(globalThis.location.href)).searchParams.get('lang') || 'en'
        );

        const guid = guids.pop();
        return service.inquiry(
          { 
            operationName: 'getCustomStyle',
            variables: { 
              txnGuid: guid!
            }, 
          },
          'customStyle'
        ).pipe(
          tap(
            ({ data: { pgwMsInquiryAction: data }}) => {
              store.setCustomStyling(data.customStyle);
              redirectTo(router, guid!, data);
            }
          )
        );
      }
    )
  ]
};
